import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationActionTemplate,
  AutomationActionType,
  ContentType,
  IExperienceDenormalizedV2,
  IExperienceMessageDenormalized,
  ITaskDenormalized,
  SubTaskType,
  TaskBlockDenormalized
} from '@dadohr/shared/domainTypes/experience';
import { compact, escapeRegExp, flatten, orderBy } from 'lodash';
import { removeMarkdown } from '../../../../../services/markdown';
import { Match, matchText } from '../../../../../services/match';

type SearchOptions = {
  excludeTitle: boolean;
  excludeContent: boolean;
  caseSensitive: boolean;
  // USER is not actively used within this search, but in the combined search it can come up here
  // It practically disables searching within the instance
  filterByType: ('USER' | 'MSG' | 'TASK')[]; // empty searches for all
  contextLength: number;
};

type MatchedBlock = {
  blockId: 'TITLE' | string;
  // type?
  match: Match;
};
export type MatchedItem =
  | {
      type: 'MSG';
      item: IExperienceMessageDenormalized;
      matches: MatchedBlock[];
    }
  | {
      type: 'TASK';
      item: ITaskDenormalized;
      matches: MatchedBlock[];
    };

// Using remark's strip-markdown would be more reliable - but it's async, therefore not really an option
const stripMarkdown = (x: string) =>
  removeMarkdown(x, {
    patternsToIgnore: ['inlineLinks']
  });

const getPlainBlockText = (block: TaskBlockDenormalized): string => {
  switch (block.type) {
    case ContentType.HEADING:
      return stripMarkdown(block.data.t);
    case ContentType.DIVIDER:
      return '';
    case ContentType.TEXT:
      return stripMarkdown(block.data.t.t);
    case ContentType.CALLOUT:
      return stripMarkdown(block.data.t.t);
    case ContentType.IMAGE:
      return stripMarkdown(block.data.alt);
    case SubTaskType.CALENDLY_INVITE:
      return block.data.label;
    case SubTaskType.CONFIRM:
      return block.data.label;
    case SubTaskType.CREATE_MEETING:
      return `${stripMarkdown(block.data.title)} ${stripMarkdown(
        block.data.description
      )}`;
    case SubTaskType.DATA_FIELD_CHANGE:
      return stripMarkdown(block.data.instructions.t);
    case SubTaskType.DOCUMENT_READ:
      return block.data.title; // also add file name?
    case SubTaskType.QUESTION:
      const label = stripMarkdown(block.data.question.label);
      if (
        block.data.question.type === 'select' ||
        block.data.question.type === 'multiSelect'
      ) {
        const options = block.data.question.options.map((o) => o.label);
        return `${label} ${options.join(' ')}`;
      }
      return label;
    case SubTaskType.SUBMIT:
      return block.data.label; // also add file name pattern?
    case SubTaskType.SWAG_UP_CHOICE:
      return block.data.question.choices.map((x) => x.product_name).join(' ');
    case SubTaskType.VISIT:
      return block.data.label === block.data.url
        ? block.data.label
        : `${block.data.label} (${block.data.url})`;
    case SubTaskType.WATCH:
      return block.data.label === block.data.url
        ? block.data.label
        : `${block.data.label} (${block.data.url})`;
    default:
      console.log('UNSUPPORTED_BLOCK_TYPE', block);
      return '';
  }
};

const matchBlock = (
  block: TaskBlockDenormalized,
  query: RegExp,
  contextLength: number
): MatchedBlock[] => {
  const txt = getPlainBlockText(block);
  const matches = matchText(txt, query, contextLength);
  return matches.map((match) => ({ blockId: block.id, match }));
};

const getPlainActionText = (action: AutomationActionTemplate) => {
  // TODO this is incomplete still
  switch (action.type) {
    case AutomationActionType.ASANA_CREATE_TASK:
      return '';
    case AutomationActionType.ATLASSIAN_CREATE_ISSUE:
      return '';
    case AutomationActionType.GOOGLE_ADD_TO_GROUP:
      return action.group.name;
    case AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION:
      return `${action.calendar.name} - ${action.query}`;
    case AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT:
      return `${action.content.type} | ${action.content.name}`;
    case AutomationActionType.OKTA_ADD_TO_GROUP:
      return action.group.name;
    case AutomationActionType.SLACK_INVITE_TO_CHANNEL:
      const channelName =
        action.channel.type === 'EXISTING'
          ? action.channel.name
          : action.channel.nameSchema;
      return channelName;
    case AutomationActionType.UNENROLL_FROM_EXPERIENCE:
      return '';
    default:
      console.log('UNSUPPORTED_ACTION_TYPE', action);
      return '';
  }
};

const matchAction = (
  action: AutomationActionTemplate,
  query: RegExp,
  contextLength: number
): MatchedBlock[] => {
  const txt = getPlainActionText(action);
  const matches = matchText(txt, query, contextLength);
  return matches.map((match) => ({ blockId: action.id, match }));
};

const matchTitle = (txt: string, query: RegExp): MatchedBlock[] => {
  // titles are always shown in full without taking a contextLength into account
  const matches = matchText(txt, query, 0);
  return matches.map((match) => ({ blockId: 'TITLE', match }));
};

const matchTask = (
  task: ITaskDenormalized,
  query: RegExp,
  opts: SearchOptions
): MatchedItem | null => {
  const matches = compact([
    ...(opts.excludeTitle ? [] : matchTitle(task.title, query)),
    ...(opts.excludeContent
      ? []
      : flatten(
          task.blocks.map((b) => matchBlock(b, query, opts.contextLength))
        ))
  ]);
  if (!matches.length) {
    return null;
  }
  return {
    type: 'TASK',
    item: task,
    matches
  };
};

const matchMessage = (
  msg: IExperienceMessageDenormalized,
  query: RegExp,
  opts: SearchOptions
): MatchedItem | null => {
  const matches = compact([
    ...(opts.excludeTitle ? [] : matchTitle(msg.subject, query)),
    ...(opts.excludeContent
      ? []
      : flatten(
          msg.blocks.map((b) => matchBlock(b, query, opts.contextLength))
        ))
  ]);
  if (!matches.length) {
    return null;
  }
  return {
    type: 'MSG',
    item: msg,
    matches
  };
};

const SORTERS = {
  day: (d: MatchedItem) => {
    return d.type === 'MSG'
      ? (d.item.sendAt?.toMillis() || 0)
      : (d.item.start?.toMillis() || 0);
  },
  type: (d: MatchedItem) => {
    if (d.type === 'MSG') {
      return 0;
    }
    if (d.type === 'TASK') {
      return 1;
    }
    return Infinity;
  }
};

export const searchInInstance = (
  d: Doc<IExperienceDenormalizedV2>,
  query: string,
  searchOptions: Partial<SearchOptions> = {}
): MatchedItem[] => {
  const opts: SearchOptions = {
    excludeTitle: searchOptions.excludeTitle ?? false,
    excludeContent: searchOptions.excludeContent ?? false,
    caseSensitive: searchOptions.caseSensitive ?? false,
    filterByType: searchOptions.filterByType ?? [],
    contextLength: searchOptions.contextLength ?? 40
  };
  const regexp = new RegExp(
    escapeRegExp(query),
    opts.caseSensitive ? 'g' : 'gi'
  );
  const tasks =
    !opts.filterByType.length || opts.filterByType.includes('TASK')
      ? d.data.tasks
      : [];
  const messages =
    !opts.filterByType.length || opts.filterByType.includes('MSG')
      ? d.data.messages
      : [];
  return orderBy(
    [
      ...compact(tasks.map((t) => matchTask(t, regexp, opts))),
      ...compact(messages.map((m) => matchMessage(m, regexp, opts)))
    ],
    [SORTERS.day, SORTERS.type]
  );
};
